// The data rules behind the Json editor field, kept out of the component.
//
// These decide whether a value can be shown as named fields and how rows collapse
// back into an object — the parts that can silently corrupt a document, so they
// live here as pure functions with tests rather than inside a React class that
// cannot be imported under jest (src/i18n.js uses top-level await).

/**
 * The object if it is a flat map of string values, otherwise null.
 *
 * Only strings qualify, deliberately. The values here are things like card
 * numbers and IBANs: inferring types from text would render "4111111111111111"
 * as a float and lose digits, and would eat the leading zero off an account
 * number. Anything carrying a number, boolean, array or nested object is
 * raw-JSON-only, where its types survive untouched.
 *
 * Absent values count as an empty object so a new record starts in fields mode.
 */
export const asFlatStringObject = (value) => {
    if (value === null || value === undefined || value === '') return {};
    if (typeof value !== 'object' || Array.isArray(value)) return null;

    const entries = Object.entries(value);
    return entries.every(([, v]) => typeof v === 'string')
        ? Object.fromEntries(entries)
        : null;
};

/**
 * Rows -> object.
 *
 * A blank key means a row that has been added but not named yet, so it is
 * dropped rather than posted as "". Keys are trimmed; values never are, because
 * requisites can legitimately carry padding. A repeated key keeps the last row,
 * which is the only resolution an object can represent.
 */
export const rowsToObject = (rows) => {
    const out = {};
    rows.forEach(({ key, value }) => {
        const name = key.trim();
        if (name !== '') out[name] = value;
    });
    return out;
};

/** The value as editable JSON text. An unparsed draft is shown back verbatim. */
export const toRawText = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

let nextRowId = 0;

/** A fresh, empty row. Ids are generated here so the whole row model lives together. */
export const newRow = (key = '', value = '') => {
    nextRowId += 1;
    return { id: `kv-${nextRowId}`, key, value };
};

/**
 * Object -> rows, reusing the id of any row that already holds that key.
 *
 * Stable ids are not cosmetic. The rows are React keys, so minting a new id for
 * an unchanged key unmounts that input and remounts it — which steals focus
 * mid-keystroke.
 */
export const toRows = (obj, previousRows = []) => {
    const idByKey = new Map(
        previousRows.filter((r) => r.key !== '').map((r) => [r.key, r.id])
    );

    return Object.entries(obj).map(([key, value]) => {
        const existing = idByKey.get(key);
        return existing ? { id: existing, key, value } : newRow(key, value);
    });
};

/**
 * Whether two values are the same document.
 *
 * Compared by content rather than by reference on purpose: react-hook-form's
 * Controller hands back a deep clone of an object value, so `===` is false even
 * for a value this field just emitted. Believing that meant rebuilding the rows
 * on every keystroke and losing focus after a single letter.
 */
export const sameDocument = (a, b) => {
    const normalise = (v) => (v === undefined ? null : v);
    try {
        return JSON.stringify(normalise(a)) === JSON.stringify(normalise(b));
    } catch {
        return false;
    }
};
